import type {
  AircraftStatus,
  Airspace,
  DispatchAssignment,
  EventEffect,
  EventScopeType,
  FlightLeg,
  GlobalEvent,
  CustomFlight,
  CustomFlightKind,
  RotationExecution,
  Severity,
  WorldData
} from "../domain/types";


export function formatTime(value: string) {
  return new Date(value).toLocaleString("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
}

export function formatMoney(value: number) {
  const sign = moneySign(value);
  return `${sign}$${Math.abs(Math.round(value)).toLocaleString("en-US")}`;
}

const severityLabels: Record<Severity, string> = {
  info: "信息",
  watch: "关注",
  warning: "警告",
  critical: "严重"
};

const eventStatusLabels: Record<GlobalEvent["status"], string> = {
  draft: "草稿",
  active: "生效中",
  cancelled: "已取消",
  expired: "已过期"
};

const scopeTypeLabels: Record<EventScopeType, string> = {
  global: "全局",
  region: "区域",
  airport: "机场",
  airspace: "空域",
  fuel_zone: "油区"
};

const airspaceStatusLabels: Record<string, string> = {
  open: "开放",
  restricted: "限制运行",
  closed: "关闭"
};

const airspaceRiskLabels: Record<Airspace["risk"], string> = {
  normal: "常规",
  busy: "繁忙",
  restricted: "受限"
};

const fuelPolicyLabels: Record<DispatchAssignment["fuelPolicy"], string> = {
  minimum_cost: "最低综合成本",
  cheapest_enroute: "沿途低价补油",
  home_tankering: "基地多带油",
  resilience: "韧性备油"
};

const eventEffectTypeLabels: Record<EventEffect["type"], string> = {
  demand_multiplier: "客流倍率",
  fuel_price_delta: "油价调整",
  airspace_status: "空域状态",
  airport_capacity_multiplier: "机场容量",
  license_pressure: "航权压力",
  reputation_delta: "声誉变化",
  maintenance_disruption: "维修扰动"
};

const customFlightKindLabels: Record<CustomFlightKind, string> = {
  recovery: "恢复航班",
  positioning: "空机调机"
};

const customFlightStatusLabels: Record<CustomFlight["status"], string> = {
  open: "待处理",
  assigned: "已派遣",
  completed: "已完成",
  failed: "失败",
  cancelled: "已取消"
};

const aircraftStatusLabels: Record<AircraftStatus, string> = {
  available: "可用",
  in_flight: "飞行中",
  maintenance: "维修中",
  grounded: "停场"
};

const rotationStatusLabels: Record<RotationExecution["status"], string> = {
  planned: "已计划",
  operated: "已执行",
  delayed: "延误",
  cancelled: "已取消",
  failed: "失败"
};

const legStatusLabels: Record<FlightLeg["status"], string> = {
  planned: "计划",
  operated: "已执行",
  delayed: "延误",
  cancelled: "取消"
};

const auditActionLabels: Record<string, string> = {
  "event.create": "创建事件",
  "event.activate": "激活事件",
  "event.cancel": "取消事件",
  "flight_plan.create": "下发航班计划",
  "flight_plan.cancel": "停用航班计划",
  "flight_cancellation.create": "取消航段",
  "flight_occurrence_event.create": "下发单班事件",
  "aircraft.create": "新增飞机",
  "aircraft.update": "更新飞机",
  "aircraft.delete": "删除飞机",
  "airport_config.update": "更新机场配置"
};

const auditTargetLabels: Record<string, string> = {
  global_event: "事件",
  flight_plan: "航班计划",
  flight_cancellation: "航段取消",
  flight_occurrence: "具体航班",
  aircraft: "飞机",
  airport: "机场"
};

export function formatSeverity(value: Severity): string {
  return severityLabels[value];
}

export function formatEventStatus(value: GlobalEvent["status"]): string {
  return eventStatusLabels[value];
}

export function formatScopeType(value: EventScopeType): string {
  return scopeTypeLabels[value];
}

export function formatScopeValue(world: WorldData, scopeType: EventScopeType, value: string): string {
  if (scopeType === "global") return "全局";
  if (scopeType === "region") {
    const region = world.regions.find((item) => item.id === value);
    return formatRegionScopeValue(region, value);
  }
  if (scopeType === "airport") {
    const airport = world.airports.find((item) => item.code === value || item.icao === value);
    return formatAirportScopeValue(airport, value);
  }
  if (scopeType === "airspace") {
    const airspace = world.airspaces.find((item) => item.id === value);
    return formatAirspaceScopeValue(airspace, value);
  }
  const zone = world.fuelZones.find((item) => item.id === value);
  return formatFuelZoneScopeValue(zone, value);
}

export function formatScope(world: WorldData, scopeType: EventScopeType, value: string): string {
  return `${formatScopeType(scopeType)}：${formatScopeValue(world, scopeType, value)}`;
}

export function formatAirspaceStatus(value: string): string {
  return airspaceStatusLabels[value] ?? value;
}

export function formatAirspaceRisk(value: Airspace["risk"]): string {
  return airspaceRiskLabels[value];
}

export function formatFuelPolicy(value: DispatchAssignment["fuelPolicy"]): string {
  return fuelPolicyLabels[value];
}

export function formatEventEffectType(value: EventEffect["type"]): string {
  return eventEffectTypeLabels[value];
}

export function formatCustomFlightKind(value: CustomFlightKind): string {
  return customFlightKindLabels[value];
}

export function formatCustomFlightStatus(value: CustomFlight["status"]): string {
  return customFlightStatusLabels[value];
}

export function formatAircraftStatus(value: AircraftStatus): string {
  return aircraftStatusLabels[value];
}

export function formatRotationStatus(value: RotationExecution["status"]): string {
  return rotationStatusLabels[value];
}

export function formatLegStatus(value: FlightLeg["status"]): string {
  return legStatusLabels[value];
}

export function formatProgramStatus(active: boolean): string {
  if (active) {
    return "运行中";
  }
  return "已取消";
}

export function formatAuditAction(value: string): string {
  return auditActionLabels[value] ?? value;
}

export function formatAuditTarget(value: string): string {
  return auditTargetLabels[value] ?? value;
}

export function formatEffect(effect: EventEffect): string {
  if (effect.type === "demand_multiplier") return `客流 x${effect.multiplier}`;
  if (effect.type === "fuel_price_delta") return `油价 ${deltaSign(effect.usdPerTonDelta)}${effect.usdPerTonDelta}/t`;
  if (effect.type === "airspace_status") return `空域${formatAirspaceStatus(effect.status)}`;
  if (effect.type === "airport_capacity_multiplier") return `容量 x${effect.multiplier}`;
  if (effect.type === "license_pressure") return `航权 ${effect.progressDelta}`;
  if (effect.type === "reputation_delta") return `声誉 ${effect.points}`;
  return `维修扰动 ${Math.round(effect.defectRate * 100)}%`;
}

function moneySign(value: number): string {
  if (value < 0) {
    return "-";
  }
  return "";
}

function formatRegionScopeValue(region: WorldData["regions"][number] | undefined, fallback: string): string {
  if (region) {
    return `${region.nameZh}（${region.id}）`;
  }
  return fallback;
}

function formatAirportScopeValue(airport: WorldData["airports"][number] | undefined, fallback: string): string {
  if (airport) {
    return `${airport.cityZh}${airport.nameZh}（${airport.code}）`;
  }
  return fallback;
}

function formatAirspaceScopeValue(airspace: WorldData["airspaces"][number] | undefined, fallback: string): string {
  if (airspace) {
    return `${airspace.nameZh}（${airspace.id}）`;
  }
  return fallback;
}

function formatFuelZoneScopeValue(zone: WorldData["fuelZones"][number] | undefined, fallback: string): string {
  if (zone) {
    return `${zone.nameZh}（${zone.id}）`;
  }
  return fallback;
}

function deltaSign(value: number): string {
  if (value >= 0) {
    return "+";
  }
  return "";
}
